/**
 * The public API, described declaratively.
 *
 * The console renders forms, chips and validation from this table alone, so
 * adding an endpoint is a data change rather than a UI change.
 */
import type { ReactNode } from "react";

export type FieldType = "text" | "textarea" | "checkbox" | "select";
export type ParamIn = "path" | "query" | "body";

export interface Field {
  name: string;
  label: string;
  type: FieldType;
  param: ParamIn;
  placeholder?: string;
  hint?: string;
  optional?: boolean;
  /** Prefilled from the last successful /add, so the four-step flow chains. */
  autoFill?: boolean;
  default?: boolean;
  options?: { value: string; label: string }[];
}

export interface Endpoint {
  key: string;
  method: "GET" | "POST";
  path: string;
  auth: boolean;
  desc: string;
  detail: string;
  /** Which balance a successful call debits, if any. */
  credit: "broxi_credit" | "youtube_credit" | null;
  flow: string | null;
  webhook: boolean;
  slow: boolean;
  response: string;
  /** Rendered beside the fields, for what the form deliberately does NOT ask. */
  note?: ReactNode;
  fields: Field[];
}

export const ENDPOINTS: Endpoint[] = [
  {
    key: "health",
    method: "GET",
    path: "/health",
    auth: false,
    desc: "Health check",
    detail: "Returns server health status. Use this to verify the API is running and reachable.",
    credit: null,
    flow: null,
    webhook: false,
    slow: false,
    response: '{ "status": "healthy" }',
    fields: [],
  },
  {
    key: "keyInfo",
    method: "GET",
    path: "/key/info",
    auth: false,
    desc: "Key info & credits",
    detail:
      "Retrieves key information including remaining broxi_credit, youtube_credit and spotify_credit balances.",
    credit: null,
    flow: null,
    webhook: false,
    slow: false,
    response: "{ id, broxi_credit, youtube_credit, spotify_credit, created_at, updated_at }",
    fields: [
      {
        name: "id",
        label: "id",
        type: "text",
        param: "query",
        placeholder: "key UUID",
        hint: "UUID of the API key to inspect",
      },
    ],
  },
  {
    key: "add",
    method: "POST",
    path: "/add",
    auth: true,
    desc: "Add YouTube URL",
    detail:
      "Checks if a YouTube URL exists in the database. If new, saves it and returns a video_id. If it exists, returns the existing data unchanged. Always the first step.",
    credit: "broxi_credit",
    flow: "Step 1 of 4",
    webhook: false,
    slow: false,
    response: "{ status, message, video_id, data?, previous_kind? }",
    // No kind picker. The console used to ask which rules /scan should judge the
    // work by, before anything had been fetched — so the answer came from
    // reading the URL, which is the one moment there is least to go on. /check
    // decides it from the title, description and transcript instead. Asking here
    // now would only be a chance to contradict that from a worse position.
    fields: [
      {
        name: "youtube_url",
        label: "youtube_url",
        type: "text",
        param: "body",
        placeholder: "https://www.youtube.com/watch?v=...",
      },
    ],
  },
  {
    key: "check",
    method: "POST",
    path: "/check",
    auth: true,
    desc: "Fetch metadata",
    detail:
      "Fetches video metadata (title, description, channel, views, transcript, etc.) from BroxiAI Agent. Required before running /scan. This is also where the work's kind is decided — classified from the fetched title, description and transcript, settling on music if it cannot tell. A kind already set is never overwritten; clear it in the admin panel to have /check decide again.",
    credit: "broxi_credit",
    flow: "Step 2 of 4",
    webhook: false,
    slow: false,
    response:
      "{ status, message, data: { id, title, description, channel_name, views, captions, kind, ... } }",
    fields: [
      {
        name: "video_id",
        label: "video_id",
        type: "text",
        param: "body",
        autoFill: true,
        placeholder: "auto-filled from /add",
      },
    ],
  },
  {
    key: "scan",
    method: "POST",
    path: "/scan",
    auth: true,
    desc: "AI scan (max 20)",
    detail:
      "Identifies the song, its artist and its composer, then searches YouTube with a 20-item limit. Each candidate is screened, its real content fetched, and verified against the original before anything is saved. On success it continues automatically into a wide 200-item pass, merged into the same scan_id — status goes starting (20 running) → presuccess (20 done) → running (200 running) → successed (combined, terminal). Webhook is disabled — poll /scan/{scan_id}.",
    credit: "youtube_credit",
    flow: "Step 3 of 4",
    webhook: false,
    slow: true,
    response: "{ status, message, video_id, scan_id }",
    fields: [
      {
        name: "video_id",
        label: "video_id",
        type: "text",
        param: "body",
        autoFill: true,
        placeholder: "auto-filled from /add",
      },
      {
        name: "scan_id",
        label: "scan_id",
        type: "text",
        param: "body",
        optional: true,
        placeholder: "optional — echoed back",
        hint: "Custom ID echoed in the response and webhook payload",
      },
      { name: "is_sandbox", label: "sandbox mode", type: "checkbox", param: "body", default: false },
    ],
  },
  {
    key: "scanStatus",
    method: "GET",
    path: "/scan/{scan_id}",
    auth: false,
    desc: "Scan job status",
    detail:
      "Returns the current status of a scan job (starting / presuccess / running / successed / failed) and progress counters. Only successed and failed are terminal — presuccess just means the 20-item pass is done and the 200-item one is next. No authentication, no credit usage.",
    credit: null,
    flow: "Polling",
    webhook: false,
    slow: false,
    response:
      "{ scan_id, video_id, status, message, total_results, verified_videos, saved_videos, verified_title, created_at, updated_at }",
    fields: [
      {
        name: "scan_id",
        label: "scan_id",
        type: "text",
        param: "path",
        placeholder: "scan ID returned by /scan",
      },
    ],
  },
  {
    key: "get",
    method: "GET",
    path: "/get",
    auth: true,
    desc: "Video & detected results",
    detail:
      "Retrieves the original video data plus all detected similar / duplicate videos found by /scan.",
    credit: null,
    flow: null,
    webhook: false,
    slow: false,
    response: "{ status, data: { original_video: {...}, detected_videos: [...] } }",
    fields: [
      {
        name: "video_id",
        label: "video_id",
        type: "text",
        param: "query",
        autoFill: true,
        placeholder: "auto-filled from /add",
      },
    ],
  },
  {
    key: "violated",
    method: "GET",
    path: "/violated/{video_id}",
    auth: true,
    desc: "Detected videos for a work",
    detail:
      'Lists every detected video recorded against one protected work — the same rows the admin panel shows under Matches, newest first. Requires an API key but uses no credits. Add ?status=pending to narrow the list; note that "owner" rows are the rights holder\'s own re-uploads, not infringements, so they are included in items but counted separately in status_counts.',
    credit: null,
    flow: null,
    webhook: false,
    slow: false,
    response:
      "{ status, video_id, youtube_url, title, channel_name, scan_status, total, status_counts: { pending, owner, report, ... }, items: [ { id, detected_video_id, detected_video_url, detected_video_title, detected_channel_name, detected_video_duration, detected_video_views, status, waiting7d, ... } ] }",
    fields: [
      {
        name: "video_id",
        label: "video_id",
        type: "text",
        param: "path",
        autoFill: true,
        placeholder: "auto-filled from /add",
      },
      {
        name: "status",
        label: "status",
        type: "text",
        param: "query",
        optional: true,
        placeholder: "optional — e.g. pending",
        hint: "Filter rows by status: pending | owner | report | revoke | pass | accepted | canceled | unavailable. Omit to return all.",
      },
    ],
  },
  {
    key: "contact",
    method: "POST",
    path: "/contact",
    auth: true,
    desc: "Channel contact info",
    detail:
      "Fetches channel contact details (emails, socials, network / MCN, metadata). Accepts a YouTube channel URL, @handle, custom name, or channel ID (UC…).",
    credit: "broxi_credit",
    flow: null,
    webhook: false,
    slow: true,
    response: "{ status, message, data: { channelId, handle, title, emails, socials, network, ... } }",
    fields: [
      {
        name: "channel",
        label: "channel",
        type: "text",
        param: "body",
        placeholder: "@MrBeast or UCX6OQ3DkcsbYNE6H8uQQuVA",
        hint: "URL, @handle, custom name, or UC… channel ID",
      },
    ],
  },
  {
    key: "report",
    method: "POST",
    path: "/report",
    auth: true,
    desc: "Submit IP report",
    detail:
      'Saves IP ownership info, updates the status of detected videos, and (optionally) flags each row as awaiting the platform\'s 7-day takedown response. Valid statuses: "report" (flag for takedown), "revoke" (revoke claim), "pass" (mark as OK). Each report_list value can be either a plain status string OR an object {status, waiting7d:true|false}.',
    credit: "broxi_credit",
    flow: "Step 4 of 4",
    webhook: false,
    slow: false,
    response: "{ status, message, updated_videos, skipped, submission_timestamp }",
    fields: [
      {
        name: "video_id",
        label: "video_id",
        type: "text",
        param: "body",
        autoFill: true,
        placeholder: "auto-filled from /add",
      },
      {
        name: "ip_info",
        label: "ip_info",
        type: "text",
        param: "body",
        placeholder: "Original content description",
      },
      {
        name: "ip_cert",
        label: "ip_cert",
        type: "text",
        param: "body",
        placeholder: "Certificate URL or ID",
      },
      {
        name: "report_list",
        label: "report_list",
        type: "textarea",
        param: "body",
        placeholder:
          '{\n  "detected-uuid-1": { "status": "report", "waiting7d": true },\n  "CGEm3-0p5fA":     { "status": "pass",   "waiting7d": false },\n  "detected-uuid-3": "revoke"\n}',
        hint: 'Map of detected video key → status. Keys may be either the detected row UUID or its YouTube video id (detected_video_id); both are matched within this video only. Each value is either a status string ("report" | "revoke" | "pass") or an object { status, waiting7d:true|false }. The waiting7d flag is shown alongside status on detected rows and persisted on the row. Keys that match nothing come back in the response\'s "skipped" array and drop status to "partial".',
      },
    ],
  },
  {
    key: "lyrics",
    method: "POST",
    path: "/lyrics",
    auth: true,
    desc: "Research song lyrics",
    detail:
      "Looks a song up on the licensed lyrics catalogue (lyrics.lyricfind.com) through the Jina research server at deepresearch.broxi.ai, and returns the lyric text it can retrieve along with the page it came from. Naming the artist separates songs that share a title. Read `retrieval` before `lyrics`: the catalogue blocks automated reads of its lyric pages, so a successful lookup usually answers retrieval=\"blocked\", where the song is identified, its canonical page is linked and `excerpt` carries the lyric text the search index published. `lyrics` holds the full sheet only when retrieval=\"ok\". A song with no page on the catalogue is a 404 and is refunded.",
    credit: "broxi_credit",
    flow: null,
    webhook: false,
    slow: true,
    response:
      '{ status, query, source, retrieval, message, match: { artist, song, url }, excerpt, lyrics, candidates: [...] }',
    fields: [
      {
        name: "song",
        label: "song",
        type: "text",
        param: "body",
        optional: true,
        placeholder: "Bohemian Rhapsody",
        hint: "Song title. Required unless you give a url instead.",
      },
      {
        name: "artist",
        label: "artist",
        type: "text",
        param: "body",
        optional: true,
        placeholder: "Queen",
        hint: "Optional, but worth sending — the catalogue holds a page per recording, so a common title returns pages by several acts. An accented artist name can narrow the search to nothing, in which case the lookup retries on the song alone.",
      },
      {
        name: "url",
        label: "url",
        type: "text",
        param: "body",
        optional: true,
        placeholder: "https://lyrics.lyricfind.com/lyrics/…",
        hint: "Read a known catalogue page directly, skipping the search. Use it when you already have the page: the search only sees what the search engine indexed, and that index does not cover the whole catalogue, so a page that exists can be unreachable by any query. Must be a /lyrics/ page on the configured source.",
      },
    ],
  },
];

export const endpointByKey = (key: string): Endpoint =>
  ENDPOINTS.find((e) => e.key === key) ?? ENDPOINTS[0]!;
